import { cn } from '@wellesley/ui'
import { AppsLink, AppsRoutes } from '@wellesley/router'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { rootReducer } from '@wellesley/store'
import { useDispatch, useSelector } from 'react-redux'
import { Avatar } from './Avatar'
import { ReactNode } from 'react'
import { LucideIcon } from 'lucide-react'
type ClickOrLink =
  | {
      onClick: () => void
      link?: undefined
    }
  | {
      onClick?: undefined
      link: string
    }

const initialState: {
  activeItem: string | null
} = {
  activeItem: null,
}

const {
  actions: { setActiveItem },
  selectors: { selectIsActive },
} = createSlice({
  name: 'toolbarItem',
  initialState,
  reducers: {
    setActiveItem: (state, action: PayloadAction<string>) => {
      state.activeItem = action.payload
    },
  },
  selectors: {
    selectIsActive: (state, id: string) => state.activeItem === id,
  },
}).injectInto(rootReducer)

export { setActiveItem }

export type ToolbarItemProps = ClickOrLink & {
  id: string
  name?: string
  Icon?: string | null | LucideIcon
  className?: string
  hasNotification?: boolean
  variant?: 'default' | 'notification'
}

export const ToolbarItem = ({
  id,
  name,
  Icon,
  link,
  className,
  variant = 'default',
  onClick,
}: ToolbarItemProps) => {
  const dispatch = useDispatch()
  const handleClick = () => {
    dispatch(setActiveItem(id))
    onClick?.()
  }
  const isActive = useSelector(
    (state: { toolbarItem: { activeItem: string | null } }) =>
      selectIsActive(state, id),
  )

  const Container = ({ children }: { children: ReactNode }) =>
    link ? (
      <AppsLink to={link as AppsRoutes}>{children}</AppsLink>
    ) : (
      <div onClick={handleClick}>{children}</div>
    )

  return (
    <div
      className={cn(
        'group relative my-3 flex cursor-pointer items-center justify-center rounded-md',
        variant === 'notification' && isActive ? 'bg-blue-500 text-white' : '',
        className,
      )}
    >
      <div className="relative flex w-full items-center justify-center">
        <div
          className={cn(
            'absolute left-0 top-1/2 h-0  w-1 -translate-y-1/2 rounded-e-sm bg-blue_100 ',
            {
              'h-8': isActive,
              'group-hover:h-4': !isActive,
            },
          )}
        />
        <Container>
          <Avatar name={name} Icon={Icon} isActive={isActive} />
        </Container>
      </div>
      <div />
    </div>
  )
}
